// PR-P0-5 reference: UC-2 (Exec Status), UC-3 (Register) · ADR-0005, ADR-0011 · docs/feature-status-and-gaps.md §2.2, §2.3
// Mockup: .claude/skills/ux-adversary/runs/uc-2-exec-status/
// Systems Architecture: §5.3, §5.4
import { FilterService } from './filter-service.js';
import { ScoringService } from './scoring-service.js';
import { RoadmapService } from './roadmap-service.js';
import { ProfileService } from './profile-service.js';
import { OutcomeService } from './outcome-service.js';
import { MethodologyService, parseFormula, restate } from './methodology-service.js';
import { insertIgnore } from '../utils/sql-dialect.js';
const WINDOW_TO_DAYS = {
    '4w': 28,
    '8w': 56,
    '12w': 84,
    '26w': 182,
    '90d': 90,
    '180d': 180,
    '1y': 365,
};
export function isHistoryWindow(input) {
    return typeof input === 'string' && input in WINDOW_TO_DAYS;
}
function windowToSinceIso(window) {
    const days = WINDOW_TO_DAYS[window];
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    return since.toISOString();
}
/**
 * Assessment Service — orchestrator for a profile's readiness lifecycle.
 *
 * Data flow:
 *   profile + master register → FilterService.filter()
 *   → persist customer_register_items (idempotent: preserve status)
 *   → ScoringService.assess() → ReadinessAssessment
 *   → RoadmapService.generate() → RoadmapResult
 *
 * Uses the IDatabase adapter for persistence; all compute is delegated
 * to the pure services so the same logic runs identically on-prem.
 */
export class AssessmentService {
    db;
    filterSvc = new FilterService();
    scoringSvc = new ScoringService();
    roadmapSvc = new RoadmapService();
    profileSvc;
    outcomeSvc;
    constructor(db) {
        this.db = db;
        this.profileSvc = new ProfileService(db);
        this.outcomeSvc = new OutcomeService(db);
    }
    /**
     * Materialize customer_register_items for a profile from the master register.
     *
     * Idempotent: an item that already exists for the profile keeps its status
     * and notes; only priority_weight, applicability_reason, and
     * applicable_frameworks are refreshed. Items no longer applicable are
     * marked 'not_applicable' rather than deleted so audit history survives.
     */
    async materialize(profileId) {
        const profile = await this.profileSvc.getById(profileId);
        if (!profile)
            throw new Error(`Profile ${profileId} not found`);
        const masterRegister = await this.loadMasterRegister();
        const filtered = this.filterSvc.filter(profile, masterRegister);
        const existing = await this.db.query('SELECT * FROM customer_register_items WHERE profile_id = ?1', [profileId]);
        const existingById = new Map(existing.results.map((r) => [r.risk_item_id, r]));
        let inserted = 0;
        let updated = 0;
        let deactivated = 0;
        const applicableIds = new Set(filtered.applied.map((a) => a.item.item_id));
        for (const row of filtered.applied) {
            const prior = existingById.get(row.item.item_id);
            const reasonJson = JSON.stringify(row.applicability_reasons);
            const frameworksJson = JSON.stringify(row.applicable_frameworks);
            const now = new Date().toISOString();
            if (prior) {
                await this.db.execute(`UPDATE customer_register_items SET
             priority_weight = ?,
             applicability_reason = ?,
             applicable_frameworks = ?,
             updated_at = ?
           WHERE profile_id = ? AND risk_item_id = ?`, [
                    row.priority_weight,
                    reasonJson,
                    frameworksJson,
                    now,
                    profileId,
                    row.item.item_id,
                ]);
                updated++;
            }
            else {
                await this.db.execute(`INSERT INTO customer_register_items (
             id, profile_id, risk_item_id, status,
             applicability_reason, priority_weight, applicable_frameworks,
             notes, updated_at
           ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
                    `cri-${profileId}-${row.item.item_id}`,
                    profileId,
                    row.item.item_id,
                    'todo',
                    reasonJson,
                    row.priority_weight,
                    frameworksJson,
                    null,
                    now,
                ]);
                inserted++;
            }
        }
        for (const prior of existing.results) {
            if (!applicableIds.has(prior.risk_item_id) && prior.status !== 'not_applicable') {
                await this.db.execute(`UPDATE customer_register_items SET status = 'not_applicable', updated_at = ?
           WHERE profile_id = ? AND risk_item_id = ?`, [new Date().toISOString(), profileId, prior.risk_item_id]);
                deactivated++;
            }
        }
        return { inserted, updated, deactivated };
    }
    /**
     * V4-2-8: materialize register items scoped to a specific AI system.
     * Uses the system's classification and reporting profile to narrow
     * the applicable register items. system_id=NULL items are workspace-wide.
     */
    async materializeForSystem(profileId, systemId, classificationJson) {
        const profile = await this.profileSvc.getById(profileId);
        if (!profile)
            throw new Error(`Profile ${profileId} not found`);
        const masterRegister = await this.loadMasterRegister();
        // V6 repair R2 (ADR-0032 §2): the system's own technical classes scope
        // its register items. Fall back to the company profile only when the
        // system has no classification yet (visibility over scoring).
        const systemTypes = extractSystemTypes(classificationJson);
        const scopedProfile = systemTypes.length > 0
            ? { ...profile, service_classifications: systemTypes }
            : profile;
        const filtered = this.filterSvc.filter(scopedProfile, masterRegister);
        const existing = await this.db.query('SELECT * FROM customer_register_items WHERE profile_id = ? AND system_id = ?', [profileId, systemId]);
        const existingById = new Map(existing.results.map((r) => [r.risk_item_id, r]));
        let inserted = 0;
        let updated = 0;
        let deactivated = 0;
        const applicableIds = new Set(filtered.applied.map((a) => a.item.item_id));
        for (const row of filtered.applied) {
            const prior = existingById.get(row.item.item_id);
            const reasonJson = JSON.stringify(row.applicability_reasons);
            const frameworksJson = JSON.stringify(row.applicable_frameworks);
            const now = new Date().toISOString();
            if (prior) {
                await this.db.execute(`UPDATE customer_register_items SET
             priority_weight = ?,
             applicability_reason = ?,
             applicable_frameworks = ?,
             updated_at = ?
           WHERE profile_id = ? AND risk_item_id = ? AND system_id = ?`, [row.priority_weight, reasonJson, frameworksJson, now, profileId, row.item.item_id, systemId]);
                updated++;
            }
            else {
                await this.db.execute(`INSERT INTO customer_register_items (
             id, profile_id, risk_item_id, system_id, status,
             applicability_reason, priority_weight, applicable_frameworks,
             notes, updated_at
           ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
                    `cri-${profileId}-${row.item.item_id}-${systemId}`,
                    profileId, row.item.item_id, systemId, 'todo',
                    reasonJson, row.priority_weight, frameworksJson, null, now,
                ]);
                inserted++;
            }
        }
        for (const prior of existing.results) {
            if (!applicableIds.has(prior.risk_item_id) && prior.status !== 'not_applicable') {
                await this.db.execute(`UPDATE customer_register_items SET status = 'not_applicable', updated_at = ?
           WHERE profile_id = ? AND risk_item_id = ? AND system_id = ?`, [new Date().toISOString(), profileId, prior.risk_item_id, systemId]);
                deactivated++;
            }
        }
        return { inserted, updated, deactivated };
    }
    /**
     * Compute (or return the current) full readiness assessment for a profile.
     * Runs filter + scoring against the current status snapshot in the DB.
     */
    async assess(profileId, now) {
        const { profile, applied, statusById, maturityById, requiredById, frameworks } = await this.loadContext(profileId);
        return this.scoringSvc.assess({
            profile,
            applied,
            statusById,
            maturityById,
            requiredById,
            frameworks,
            now,
        });
    }
    async scorecard(profileId) {
        const assessment = await this.assess(profileId);
        return this.scoringSvc.toScorecard(assessment);
    }
    /**
     * Per-outcome readiness scores (EU AI Act Ready, FedRAMP Ready, etc.).
     * Reuses the framework scores already computed by assess() rather than
     * re-scoring the register.
     */
    async outcomes(profileId) {
        const [assessment, outcomes] = await Promise.all([
            this.assess(profileId),
            this.outcomeSvc.list(),
        ]);
        const profile = await this.profileSvc.getById(profileId);
        if (!profile)
            throw new Error(`Profile ${profileId} not found`);
        return this.outcomeSvc.score(profile, outcomes, assessment.framework_scores);
    }
    async roadmap(profileId, limit = 20, now) {
        const { applied } = await this.loadContext(profileId);
        const assessment = await this.assess(profileId, now);
        return this.roadmapSvc.generate(assessment, applied, { limit, now });
    }
    async progress(profileId) {
        const assessment = await this.assess(profileId);
        const bySeverity = {
            critical: { total: 0, implemented: 0, open: 0 },
            high: { total: 0, implemented: 0, open: 0 },
            elevated: { total: 0, implemented: 0, open: 0 },
            moderate: { total: 0, implemented: 0, open: 0 },
            standard: { total: 0, implemented: 0, open: 0 },
        };
        for (const g of assessment.gap_rows) {
            const bucket = bySeverity[g.severity];
            bucket.total += 1;
            if (g.status === 'done' || g.status === 'not_applicable') {
                bucket.implemented += 1;
            }
            else {
                bucket.open += 1;
            }
        }
        return {
            profile_id: profileId,
            generated_at: assessment.generated_at,
            by_status: {
                todo: assessment.totals.todo,
                in_progress: assessment.totals.in_progress,
                blocked: assessment.totals.blocked,
                review_needed: assessment.totals.review_needed,
                done: assessment.totals.done,
                not_applicable: assessment.totals.not_applicable,
                delegate_up: assessment.totals.delegate_up,
            },
            by_severity: bySeverity,
        };
    }
    async setItemStatus(profileId, itemId, update) {
        const existing = await this.db.query('SELECT * FROM customer_register_items WHERE profile_id = ?1 AND risk_item_id = ?2', [profileId, itemId]);
        const prior = existing.results[0];
        if (!prior) {
            throw new Error(`No customer register item for profile=${profileId} item=${itemId}`);
        }
        const blockedReason = update.status === 'blocked' ? (update.blocked_reason ?? null) : null;
        const naRationale = update.status === 'not_applicable' ? (update.na_rationale ?? null) : null;
        const now = new Date().toISOString();
        await this.db.execute(`UPDATE customer_register_items
          SET status = ?, notes = COALESCE(?, notes),
              blocked_reason = ?, na_rationale = ?, updated_at = ?
        WHERE profile_id = ? AND risk_item_id = ?`, [update.status, update.notes ?? null, blockedReason, naRationale, now, profileId, itemId]);
        await this.db.execute(`INSERT INTO status_changes (
         change_id, profile_id, risk_item_id,
         previous_status, new_status, changed_by, notes, changed_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`, [
            `sc-${profileId}-${itemId}-${now}`,
            profileId,
            itemId,
            prior.status,
            update.status,
            update.changed_by ?? null,
            update.notes ?? null,
            now,
        ]);
        return {
            ...prior,
            status: update.status,
            notes: update.notes ?? prior.notes,
            blocked_reason: blockedReason,
            na_rationale: naRationale,
            updated_at: now,
        };
    }
    /**
     * Persist a scoring snapshot. Called after a status change or at scheduled
     * intervals so the burn-down chart has trend data.
     *
     * Every snapshot pins the methodology version in force at write time
     * per §2.2 of docs/uc-2-provenance-api-spec.md — the as-shipped view
     * later reads this back to render the historical point under its
     * original formula. Without the pin, restatement can't tell the
     * difference between "score drifted" and "formula changed".
     *
     * The stored `framework_scores` blob is enriched with per-framework
     * `severity_totals` (4-tier: critical / high / medium / low) — the
     * shape `restate()` in methodology-service consumes to recompute a
     * historical point under a new formula. Domain severities are 5-tier
     * (critical / high / elevated / moderate / standard); the mapping
     * (elevated → high, moderate → medium, standard → low) preserves the
     * relative-weight ordering the formula families care about.
     */
    async snapshotScores(profileId) {
        const assessment = await this.assess(profileId);
        const snapshot_id = `snap-${profileId}-${assessment.generated_at}`;
        const methodologySvc = new MethodologyService(this.db);
        // Fail loudly if the methodology registry is empty — a snapshot
        // without a pin can't be restated later and silently corrupts the
        // history. Seed migration 031 ensures v1.0.0 exists on every env.
        const active = await methodologySvc.getActiveVersion();
        const enrichedFrameworks = enrichFrameworksForRestatement(assessment.framework_scores, assessment.gap_rows);
        await this.db.execute(`INSERT INTO assessment_scores (
         snapshot_id, profile_id, aggregate_score,
         framework_scores, classification_maturity, totals,
         generated_at, methodology_version
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`, [
            snapshot_id,
            profileId,
            assessment.aggregate_score,
            JSON.stringify(enrichedFrameworks),
            JSON.stringify(assessment.classification_maturity),
            JSON.stringify(assessment.totals),
            assessment.generated_at,
            active.version,
        ]);
    }
    /**
     * §3.2 readiness history — every stored snapshot with both its
     * as-shipped score (frozen under the methodology in force when it
     * was written) and its restated score (recomputed under the currently
     * active methodology). The UI toggles between them without a
     * round-trip.
     *
     * `window` is a compact window spec — '8w' / '12w' / '90d' / '1y' —
     * limiting how far back we scan. Defaults to '12w'. Invalid specs
     * throw so callers can't silently fall through to "all history".
     *
     * Restatement only happens when it's meaningful: if a point's
     * methodology_version matches the active version, restated === as-shipped
     * and we skip the recompute. When the point pre-dates the active
     * version, we call the pure `restate()` from methodology-service on
     * the stored `framework_scores` blob (per §3.2.5 the v1 restatement
     * scope is framework-rollup only).
     */
    async readinessHistory(profileId, opts = {}) {
        const window = opts.window ?? '12w';
        const sinceIso = windowToSinceIso(window);
        const methodologySvc = new MethodologyService(this.db);
        const active = await methodologySvc.getActiveVersion();
        // NULL methodology_version rows are legacy snapshots written
        // before migration 031 landed. Treat them as "unknown as-shipped
        // pin" — their as_shipped block reports version: null and
        // restated skips (no way to prove the formula shape at the point).
        const res = await this.db.query(`SELECT snapshot_id, aggregate_score, framework_scores,
              generated_at, methodology_version
         FROM assessment_scores
        WHERE profile_id = ?
          AND generated_at >= ?
        ORDER BY generated_at ASC`, [profileId, sinceIso]);
        const activeFormula = parseFormula(active.formula_json);
        const points = res.results.map((row) => {
            const asShippedVersion = row.methodology_version;
            let restatedScore;
            if (asShippedVersion === null) {
                // Legacy point without a pin — can't restate deterministically.
                restatedScore = null;
            }
            else if (asShippedVersion === active.version) {
                // Same formula as the active version — as-shipped is already
                // the restated view, no recompute needed.
                restatedScore = row.aggregate_score;
            }
            else {
                try {
                    const snapshot = { framework_scores: JSON.parse(row.framework_scores) };
                    restatedScore = restate(snapshot, activeFormula);
                }
                catch {
                    // Malformed framework_scores blob — degrade gracefully rather
                    // than break the whole series. Caller sees restated: null so
                    // the UI can render "restatement unavailable" instead of
                    // failing the endpoint.
                    restatedScore = null;
                }
            }
            return {
                snapshot_id: row.snapshot_id,
                taken_at: row.generated_at,
                as_shipped: {
                    score: row.aggregate_score,
                    methodology_version: asShippedVersion,
                },
                restated: {
                    score: restatedScore,
                    methodology_version: active.version,
                },
            };
        });
        return {
            profile_id: profileId,
            window,
            points,
            methodology: {
                active_version: active.version,
                active_effective_at: active.effective_at,
                formula_url: `/api/methodology/versions/${active.version}`,
            },
        };
    }
    /**
     * Export the full assessment as a JSON payload — the raw shape for the
     * customer to download or for a downstream PDF/XLSX generator.
     *
     * The response carries a `methodology` block pinning the version in
     * force at export time per §2.4 of docs/uc-2-provenance-api-spec.md
     * ("Every export PDF stamps the version in the footer"). Older
     * exports without a methodology pin can't be reconciled against
     * later restatements; every new export MUST carry this.
     */
    async exportJson(profileId) {
        const profile = await this.profileSvc.getById(profileId);
        if (!profile)
            throw new Error(`Profile ${profileId} not found`);
        const assessment = await this.assess(profileId);
        const { applied } = await this.loadContext(profileId);
        const roadmap = this.roadmapSvc.generate(assessment, applied);
        const outcomesList = await this.outcomeSvc.list();
        const outcomes = this.outcomeSvc.score(profile, outcomesList, assessment.framework_scores);
        const methodologySvc = new MethodologyService(this.db);
        const active = await methodologySvc.getActiveVersion();
        return {
            profile,
            assessment,
            roadmap,
            outcomes,
            methodology: {
                version: active.version,
                effective_at: active.effective_at,
                formula_url: `/api/methodology/versions/${active.version}`,
            },
        };
    }
    // --- Internals ---
    async loadContext(profileId) {
        const profile = await this.profileSvc.getById(profileId);
        if (!profile)
            throw new Error(`Profile ${profileId} not found`);
        const [masterRegister, frameworks, existing] = await Promise.all([
            this.loadMasterRegister(),
            this.loadFrameworks(),
            this.db.query('SELECT * FROM customer_register_items WHERE profile_id = ?1', [profileId]),
        ]);
        const filtered = this.filterSvc.filter(profile, masterRegister);
        const statusById = new Map();
        // maturityById: effective attained level per item. User override
        // wins over evidence-derived (ADR-0005); missing → null so the
        // scorer falls back to status-based closure for un-attested items.
        const maturityById = new Map();
        // requiredById: per-workspace override for the TARGET (required)
        // level. When set, becomes the top layer of the priority stack
        // in ScoringService.effectiveRequiredLevel. Missing → falls
        // through to the editorial framework target, then the derived
        // default from expected_evidence_types.
        const requiredById = new Map();
        for (const row of existing.results) {
            statusById.set(row.risk_item_id, row.status);
            const attained = row.maturity_override_level ?? row.derived_maturity_level ?? null;
            maturityById.set(row.risk_item_id, attained);
            requiredById.set(row.risk_item_id, row.required_maturity_override ?? null);
        }
        return {
            profile,
            applied: filtered.applied,
            statusById,
            maturityById,
            requiredById,
            frameworks,
        };
    }
    async loadMasterRegister() {
        const res = await this.db.query("SELECT * FROM risk_items WHERE status = 'active'");
        return res.results;
    }
    async loadFrameworks() {
        const res = await this.db.query("SELECT * FROM frameworks WHERE status = 'active'");
        // Normalize is_mandatory 0/1 → boolean for downstream consumers.
        return res.results.map((f) => ({
            ...f,
            is_mandatory: Boolean(f.is_mandatory),
        }));
    }
    async captureWeeklySnapshot(profileId) {
        const assessment = await this.assess(profileId);
        const now = new Date();
        const weekKey = `${now.getFullYear()}-W${String(Math.ceil((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000))).padStart(2, '0')}`;
        const snapshotId = `ash-${profileId}-${weekKey}`;
        const fwScoresJson = JSON.stringify(assessment.framework_scores.map((fs) => ({
            framework_id: fs.framework_id,
            score: fs.score,
            applicable_items: fs.applicable_items,
            done: fs.done,
            complete_count: fs.complete_count,
        })));
        const openCount = assessment.totals.todo + assessment.totals.in_progress +
            assessment.totals.blocked + assessment.totals.review_needed + assessment.totals.delegate_up;
        const doneCount = assessment.totals.done;
        const totalCount = assessment.totals.applicable;
        await this.db.execute(insertIgnore(this.db, `assessments_history (
         snapshot_id, profile_id, week_key, aggregate_score,
         framework_scores_json, open_count, done_count, total_count, captured_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`), [snapshotId, profileId, weekKey, assessment.aggregate_score,
            fwScoresJson, openCount, doneCount, totalCount, now.toISOString()]);
    }
    async getAssessmentHistory(profileId, weeks = 8) {
        const res = await this.db.query(`SELECT week_key, aggregate_score, open_count, done_count, total_count, captured_at
         FROM assessments_history
        WHERE profile_id = ?
        ORDER BY week_key DESC
        LIMIT ?`, [profileId, weeks]);
        return res.results.reverse();
    }
}
// -------------------------------------------------------------------
// Snapshot enrichment for §3.2 restatement.
// -------------------------------------------------------------------
/**
 * Map the domain's 5-tier severity taxonomy onto the 4-tier taxonomy
 * `MethodologyFormula.weights` uses. The mapping preserves the
 * relative-priority ordering the formula families care about:
 *
 *   critical → critical  (highest weight)
 *   high     → high      (regulatory / deadline-bound)
 *   elevated → high      (aggregate as "high enough to matter")
 *   moderate → medium    (mid-tier baseline)
 *   standard → low       (baseline hygiene)
 *
 * When the methodology formula gains a 5-tier `weights` shape in a
 * future version, drop this mapping and pass the domain severities
 * through unchanged.
 */
function domainSeverityToFormulaTier(severity) {
    switch (severity) {
        case 'critical':
            return 'critical';
        case 'high':
        case 'elevated':
            return 'high';
        case 'moderate':
            return 'medium';
        case 'standard':
            return 'low';
    }
}
/**
 * Enrich each stored FrameworkReadinessScore with a
 * `severity_totals: { critical, high, medium, low }` block so
 * `restate()` in methodology-service can recompute the point under a
 * new formula without needing the original gap_rows. Computed by
 * counting each framework's applicable items in the 4-tier taxonomy.
 *
 * The `framework_key` field is also added because that's the shape
 * the ScoredSnapshot type in methodology-service expects. We reuse
 * `framework_id` for both since the domain has no separate key column
 * on FrameworkReadinessScore today.
 */
function enrichFrameworksForRestatement(frameworkScores, gapRows) {
    return frameworkScores.map((fs) => {
        const relevant = gapRows.filter((r) => r.applicable_frameworks.includes(fs.framework_id));
        const severity_totals = { critical: 0, high: 0, medium: 0, low: 0 };
        for (const r of relevant) {
            severity_totals[domainSeverityToFormulaTier(r.severity)] += 1;
        }
        return {
            ...fs,
            framework_key: fs.framework_id,
            severity_totals,
        };
    });
}
/** `classification_json.types` → technical classes; tolerant of raw JSON strings. */
/** The AI system families register items scope by (ai_system_classifications.classification_type). */
export const AI_SYSTEM_FAMILIES = ['agentic_systems', 'ai_agents', 'llm_services', 'ml_models', 'data_pipelines'];
const FAMILY_SET = new Set(AI_SYSTEM_FAMILIES);
/**
 * The system's AI families from classification_json.types. Only real family
 * values count: classification saves before 2026-10-21 stored the security
 * rating keys (confidentiality / integrity / availability) here, which matched
 * no register item and emptied the system's register. Anything else is ignored,
 * so such a system falls back to the company profile's families.
 */
export function extractSystemTypes(classificationJson) {
    let v = classificationJson;
    if (typeof v === 'string') {
        try {
            v = JSON.parse(v);
        }
        catch {
            return [];
        }
    }
    const types = v?.types;
    return Array.isArray(types) ? types.map(String).filter((t) => FAMILY_SET.has(t)) : [];
}
//# sourceMappingURL=assessment-service.js.map